/**
 * FIG Formatting Utilities
 *
 * All display formatting in one place. Every table cell, stat card, and detail
 * view should use these — never raw toLocaleString() or manual string formatting.
 */

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const currencyCompactFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  minimumFractionDigits: 0,
  maximumFractionDigits: 1,
});

const numberFormatter = new Intl.NumberFormat('en-US');

const percentFormatter = new Intl.NumberFormat('en-US', {
  style: 'percent',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

/**
 * Format as USD currency: $125,000
 */
export function formatCurrency(value: number | null | undefined): string {
  if (value == null) return '—';
  return currencyFormatter.format(value);
}

/**
 * Format as compact currency: $2.5M, $125K, $500
 * Used in stat cards where space is limited.
 */
export function formatCurrencyCompact(value: number | null | undefined): string {
  if (value == null) return '—';
  return currencyCompactFormatter.format(value);
}

/**
 * Format number with commas: 1,245
 */
export function formatNumber(value: number | null | undefined): string {
  if (value == null) return '—';
  return numberFormatter.format(value);
}

/**
 * Format as percentage from decimal: 0.85 → "85%"
 */
export function formatPercent(value: number | null | undefined): string {
  if (value == null) return '—';
  return percentFormatter.format(value);
}

/**
 * Format as percentage from whole number: 85 → "85%"
 */
export function formatPercentWhole(value: number | null | undefined): string {
  if (value == null) return '—';
  return `${value}%`;
}

/**
 * Format date: "Mar 4, 2026"
 */
export function formatDate(value: string | Date | null | undefined): string {
  if (value == null) return '—';
  const date = typeof value === 'string' ? new Date(value) : value;
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Format datetime: "Mar 4, 2026 3:45 PM"
 */
export function formatDateTime(value: string | Date | null | undefined): string {
  if (value == null) return '—';
  const date = typeof value === 'string' ? new Date(value) : value;
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Relative time: "Just now", "2m ago", "1h 30m ago", "3d ago"
 */
export function formatRelativeTime(value: string | Date | null | undefined): string {
  if (value == null) return '—';
  const date = typeof value === 'string' ? new Date(value) : value;
  if (isNaN(date.getTime())) return '—';

  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return 'Just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (hours < 24) {
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m ago` : `${hours}h ago`;
  }
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

/**
 * Format millisecond duration: "150ms", "2.3s", "1m 30s"
 */
export function formatDuration(ms: number | null | undefined): string {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms}ms`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes}m ${remainingSeconds}s`;
}

/**
 * Format a score as a string with no decimals: 85 → "85"
 * Returns "—" for null/undefined.
 */
export function formatScore(value: number | null | undefined): string {
  if (value == null) return '—';
  return Math.round(value).toString();
}
